import React, {
    useMemo,
    useState,
    useEffect
} from "react";

import {
    FormControl
} from "@material-ui/core";

import TreeSelect, {
    SHOW_PARENT
} from "rc-tree-select";

import "rc-tree-select/assets/index.css";
import deepRenameKeys from "deep-rename-keys";


/* =========================================================
   البحث عن الحسابات المطابقة
   وإيجاد الآباء الذين يجب فتحهم
   ========================================================= */

const findSearchMatchesAndParents = (
    nodes,
    searchText
) => {

    const expandedKeys = [];
    const matchedKeys = [];

    if (
        !Array.isArray(nodes) ||
        !searchText ||
        !searchText.trim()
    ) {

        return {
            expandedKeys: [],
            matchedKeys: []
        };

    }


    const search =
        searchText
            .trim()
            .toLowerCase();


    const walk = (
        list,
        parents = []
    ) => {

        if (!Array.isArray(list)) {
            return;
        }


        list.forEach(node => {

            /*
             * =============================================
             * اسم الحساب
             * =============================================
             */

            const accountName =
                String(
                    node.AName ??
                    node.account?.AName ??
                    node.label ??
                    node.title ??
                    ""
                );


            /*
             * =============================================
             * مفتاح الحساب
             * =============================================
             */

            const nodeKey =
                node.value !== undefined &&
                node.value !== null
                    ? String(node.value)
                    : null;


            /*
             * =============================================
             * هل الحساب مطابق للبحث؟
             * =============================================
             */

            const isMatch =
                accountName
                    .toLowerCase()
                    .includes(search);


            /*
             * =============================================
             * إذا الحساب مطابق
             * =============================================
             */

            if (isMatch) {

                /*
                 * تسجيل الحساب المطابق
                 */

                if (nodeKey !== null) {

                    matchedKeys.push(
                        nodeKey
                    );

                }


                /*
                 * فتح جميع الآباء
                 */

                parents.forEach(
                    parentKey => {

                        if (
                            parentKey !== null &&
                            parentKey !== undefined
                        ) {

                            expandedKeys.push(
                                String(parentKey)
                            );

                        }

                    }
                );


                /*
                 * إذا كان الحساب نفسه له أبناء
                 * نفتح الحساب أيضًا
                 */

                if (
                    node.children &&
                    node.children.length > 0 &&
                    nodeKey !== null
                ) {

                    expandedKeys.push(
                        nodeKey
                    );

                }

            }


            /*
             * =============================================
             * البحث داخل الأبناء
             * =============================================
             */

            if (
                node.children &&
                node.children.length > 0
            ) {

                walk(
                    node.children,
                    [
                        ...parents,
                        nodeKey
                    ]
                );

            }

        });

    };


    walk(nodes);


    return {

        expandedKeys: [
            ...new Set(
                expandedKeys
            )
        ],

        matchedKeys: [
            ...new Set(
                matchedKeys
            )
        ]

    };

};


/* =========================================================
   إنشاء الشجرة مع تمييز نتائج البحث

   ملاحظة مهمة:

   لا نقوم بحذف أي حساب.

   فقط نضيف class للحساب المطابق
   حتى نستطيع الوصول إليه وعمل scroll له.
   ========================================================= */

const createSearchTree = (
    nodes,
    searchText
) => {

    if (!Array.isArray(nodes)) {
        return [];
    }


    const search =
        String(
            searchText || ""
        )
            .trim()
            .toLowerCase();


    /*
     * بدون بحث
     */

    if (!search) {

        return nodes;

    }


    return nodes.map(node => {

        /*
         * =============================================
         * اسم الحساب
         * =============================================
         */

        const accountName =
            String(
                node.AName ??
                node.account?.AName ??
                node.label ??
                node.title ??
                ""
            );


        /*
         * =============================================
         * هل الحساب مطابق؟
         * =============================================
         */

        const isMatch =
            accountName
                .toLowerCase()
                .includes(search);


        /*
         * =============================================
         * العنوان
         * =============================================
         */

        let title;


        if (isMatch) {

            title = (

                <span
                    className="account-search-match"
                    data-search-match="true"

                    style={{
                        display:
                            "inline-block",

                        padding:
                            "2px 7px",

                        borderRadius:
                            "4px",

                        backgroundColor:
                            "#fff176",

                        fontWeight:
                            "bold",

                        color:
                            "#000",

                        /*
                         * مهم:
                         * حتى يستطيع scrollIntoView
                         * الوصول للحساب بشكل واضح.
                         */

                        scrollMarginTop:
                            "120px",

                        scrollMarginBottom:
                            "120px"
                    }}
                >

                    {accountName}

                </span>

            );

        } else {

            /*
             * الحساب غير مطابق
             * نحافظ على title الأصلي
             */

            title =
                node.title;

        }


        /*
         * =============================================
         * نسخة من الحساب
         * =============================================
         */

        const newNode = {

            ...node,

            title

        };


        /*
         * =============================================
         * الأبناء
         *
         * لا نحذف أي حساب.
         * =============================================
         */

        if (
            node.children &&
            node.children.length > 0
        ) {

            newNode.children =
                createSearchTree(
                    node.children,
                    searchText
                );

        }


        return newNode;

    });

};


/* =========================================================
   Scroll إلى الحساب الأصفر

   هذه هي الإضافة الأساسية الجديدة.

   بعد أن يقوم React بفتح الآباء
   وإظهار الحساب، ننتظر حتى يتم رسم DOM
   ثم نذهب إلى أول نتيجة.
   ========================================================= */

const scrollToSearchResult = (
    searchText
) => {

    /*
     * لا يوجد بحث
     */

    if (
        !searchText ||
        !searchText.trim()
    ) {

        return;

    }


    /*
     * نستخدم أكثر من محاولة لأن
     * rc-tree-select يقوم بالرسم
     * بشكل غير متزامن.
     */

    let attempts = 0;

    const maxAttempts = 10;


    const tryScroll = () => {

        attempts++;


        /*
         * الحسابات التي عليها
         * class الحساب المطابق
         */

        const matches =
            document.querySelectorAll(
                ".account-search-match"
            );


        /*
         * إذا وجدنا نتيجة
         */

        if (
            matches &&
            matches.length > 0
        ) {

            const firstMatch =
                matches[0];


            /*
             * التأكد أن العنصر ظاهر
             */

            firstMatch.scrollIntoView({

                behavior:
                    "smooth",

                block:
                    "center",

                inline:
                    "nearest"

            });


            /*
             * نضع تركيز بسيط على النتيجة
             * بدون تغيير تحديد الحساب.
             */

            firstMatch.style.outline =
                "2px solid #f9a825";


            /*
             * إزالة الإطار بعد فترة
             */

            setTimeout(() => {

                if (
                    firstMatch
                ) {

                    firstMatch.style.outline =
                        "";

                }

            }, 1000);


            return;

        }


        /*
         * إذا لم تظهر النتيجة بعد
         * نحاول مرة أخرى.
         */

        if (
            attempts < maxAttempts
        ) {

            setTimeout(
                tryScroll,
                50
            );

        }

    };


    /*
     * أول محاولة بعد انتهاء
     * عملية الرسم الحالية.
     */

    setTimeout(
        tryScroll,
        50
    );

};


/* =========================================================
   المكون الرئيسي
   ========================================================= */

   export default function AccountTreeSelected(props) {
    const { accounts,
        value,
        onChange,
        SHOW_PARENT,
        onSelect } = props;


    /* =====================================================
       بناء mData

       Code يصبح value.

       لا نعيد بناء الشجرة من ParentCode.
       ===================================================== */

    const mData = useMemo(() => {

        return deepRenameKeys(
            accounts,
            (key) => {

                if (
                    key === "Code"
                ) {

                    return "value";

                }


                return key;

            }
        );

    }, [
        accounts
    ]);


    /* =====================================================
       البحث
       ===================================================== */

    const [
        searchValue,
        setSearchValue
    ] = useState("");


    /* =====================================================
       معرفة الحسابات المطابقة
       والآباء الذين يجب فتحهم
       ===================================================== */

    const searchInfo =
        useMemo(() => {

            if (
                !searchValue ||
                !searchValue.trim()
            ) {

                return {

                    expandedKeys: [],

                    matchedKeys: []

                };

            }


            return findSearchMatchesAndParents(
                mData,
                searchValue
            );

        }, [
            mData,
            searchValue
        ]);


    /* =====================================================
       الشجرة النهائية

       جميع الحسابات تبقى موجودة.

       البحث فقط يغير شكل الحساب المطابق.
       ===================================================== */

    const finalTreeData =
        useMemo(() => {

            if (
                !searchValue ||
                !searchValue.trim()
            ) {

                return mData;

            }


            return createSearchTree(
                mData,
                searchValue
            );

        }, [
            mData,
            searchValue
        ]);


    /* =====================================================
       الحسابات المفتوحة أثناء البحث
       ===================================================== */

    const expandedKeys =
        useMemo(() => {

            if (
                !searchValue ||
                !searchValue.trim()
            ) {

                return [];

            }


            return searchInfo.expandedKeys;

        }, [
            searchInfo,
            searchValue
        ]);


    /* =====================================================
       عند تغير البحث:

       1. React يرسم الشجرة
       2. الآباء ينفتحون
       3. الحساب الأصفر يظهر
       4. نذهب إليه تلقائيًا
       ===================================================== */

    useEffect(() => {

        if (
            !searchValue ||
            !searchValue.trim()
        ) {

            return;

        }


        scrollToSearchResult(
            searchValue
        );


    }, [
        searchValue,
        expandedKeys,
        finalTreeData
    ]);


    /* =====================================================
       معالجة البحث
       ===================================================== */

    const handleSearch = (
        text
    ) => {

        setSearchValue(
            text
        );

    };


    /* =====================================================
       Render
       ===================================================== */

    return (

        <FormControl>

            <TreeSelect

                /*
                 * =========================================
                 * الشكل
                 * =========================================
                 */

                style={{

                    minWidth: 450,

                    height: 40,

                    direction: "ltr",

                    fontWeight: "bold",

                    textAlign: "center",

                    color: "black"

                }}


                /*
                 * =========================================
                 * Dropdown
                 * =========================================
                 */

                dropdownStyle={{

                    direction: "ltr",

                    maxHeight: 300,

                    overflow: "auto",

                    zIndex: 1500

                }}


                className="check-select"


                /*
                 * =========================================
                 * Animation
                 * =========================================
                 */

                transitionName={
                    "rc-tree-select-dropdown-slide-up"
                }

                choiceTransitionName={
                    "rc-tree-select-selection__choice-zoom"
                }


                /*
                 * =========================================
                 * Alignment
                 * =========================================
                 */

                dropdownPopupAlign={{

                    overflow: {

                        adjustY: 0,

                        adjustX: 0

                    },

                    offset: [

                        0,

                        2

                    ]

                }}


                /*
                 * =========================================
                 * الشجرة
                 *
                 * مهمة جدًا:
                 *
                 * لا نستخدم filterTree.
                 *
                 * جميع الحسابات موجودة.
                 * =========================================
                 */

                treeData={
                    finalTreeData
                }


                /*
                 * =========================================
                 * الحسابات المحددة
                 *
                 * البحث لا يغير value.
                 * =========================================
                 */

                value={
                    value
                }

                onChange={
                    onChange
                }


                /*
                 * =========================================
                 * Checkbox
                 * =========================================
                 */

                treeCheckable


                /*
                 * =========================================
                 * SHOW_PARENT
                 * =========================================
                 */

                showCheckedStrategy={
                    SHOW_PARENT
                }


                /*
                 * =========================================
                 * البحث
                 * =========================================
                 */

                showSearch

                searchValue={
                    searchValue
                }

                onSearch={
                    handleSearch
                }


                /*
                 * =========================================
                 * مهم جدًا:
                 *
                 * منع TreeSelect من عمل
                 * فلترة خاصة به.
                 *
                 * لأننا نريد جميع الحسابات.
                 * =========================================
                 */

                filterTreeNode={
                    () => true
                }


                /*
                 * =========================================
                 * فتح الآباء
                 *
                 * مثال:
                 *
                 * 1
                 * └── 12
                 *     └── 123
                 *         └── موسى
                 *
                 * يتم فتح:
                 *
                 * 1
                 * 12
                 * 123
                 * =========================================
                 */

                expandedKeys={
                    expandedKeys
                }


                /*
                 * =========================================
                 * Placeholder
                 * =========================================
                 */

                placeholder={
                    "قم بتحديد الحسابات"
                }


                /*
                 * =========================================
                 * لا تمسح البحث تلقائيًا
                 * =========================================
                 */

                autoClearSearchValue={
                    false
                }
                maxTagCount={0}
                maxTagPalceholder={()=>null}
                showArrow
            />

        </FormControl>

    );

}